import React, { useState, useEffect, useContext } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import { TrackingContext } from '@vrbo/react-event-tracking';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import LocationTypeahead from './LocationTypeahead';

import GoogleApiLoader from '../../../../google/maps';
import withPageFlow from '../../../PageFlow/PageFlow';
import TranslationUtil from '../../../../utils/TranslationUtil';
import ProgressBarLYP from '../../../ProgressBarLYP';
import LocationActions from '../../../../actions/location';
import ExperimentUtil from '../../../../utils/ExperimentUtil';
import MovableMap from '../../GoogleMap/MovableMap';
import { sendFlowSummary } from '../../edap';
import {
    isEnUsPermanentFeatureVrEnabled,
    isConfirmAddressFeatureEnabled
} from '../../../../utils/ExperimentsUtil';
import { createPageViewEvent, logLocationInformationEvent } from '../../../../utils/EventProviders/EgClickstreamUtil';
import LocationField from './LocationField';
import NextButton from './NextButton';
import log from '../../../../utils/ClientLogger';
import { isMobileView } from '../../../../../server/utils/UIUtil';
import { AAPLOCATION_PAGE } from '../../../PageFlow/PageList';
import { getConfigFromLocale } from '../../../../utils/LocaleUtil/googleLocaleConfig';

export { getConfigFromLocale };

export function Location(props) {
    const {
        referrer,
        latLng,
        title,
        pageFlow,
        submitText,
        locale,
        step,
        address,
        features,
        analytics,
        addressValidation,
        triggerGeoCoderValidation,
        geocoderLatLng,
        geocoderValidation,
        mapFootNote,
        setLatLng,
        triggerValidateUnitNumber,
        unitNumberValidation,
        unitCheckEnabled,
        brand,
        unitNumberLabel,
        setAddressField,
        pathSource,
        instructions,
        isAddressV2,
        typeaheadQuery,
        isAAP
    } = props;
    const [moveout, setMoveout] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [mapsApi, setMapsApi] = useState(null);
    const [userTypeaheadInput, setUserTypeaheadInput] = useState();
    const { trigger } = useContext(TrackingContext);

    const {
        start: geocoderStart,
        success: geocoderSuccess,
        fail: geocoderFail,
        value: geocoderValid
    } = geocoderValidation;

    const {
        start: unitValidationStart,
        success: unitValidationSuccess,
        fail: unitValidationFail,
        value: uNValidation
    } = unitNumberValidation;

    const pageNameDetailed = `Location.LocationValidation.${pathSource}`;
    const unitNumberKey = 'address2';
    const isFormDataValid = () => {
        const keys = Object.keys(addressValidation);
        return keys.reduce(
            (acc, k) => (
                acc && addressValidation[k].isValid),
            true
        );
    };

    const handleAddressFieldChange = (e) => {
        setAddressField(e.target.name, e.target.value, e.target.candidateLatLng);
    };

    const moveOn = () => {
        if (features.isConventionalLodgingFlow && isFormDataValid() && geocoderValid) {
            const gaData = {
                propertyAddress: address.address1,
                propertyCity: address.city,
                propertyZip: address.postalCode,
                propertyCountryRegion: address.country,
                propertyStateProvince: address.stateProvince,
                event: 'PropertyAddressSubmit',
                id: 'PropertyAddressSubmit'
            };
            trigger('gtmEvent', gaData);
        }

        if (geocoderValid && (unitCheckEnabled
            ? !unitNumberValidation.value.missingUnitNumber
                && unitNumberValidation.value.unitNumberValid
            : true)) {
            sendFlowSummary(trigger, false);
        }

        if (typeaheadQuery === userTypeaheadInput && geocoderValid) {
            log('info', 'location - typeahead on next click', {
                autocompleteSelectedTypeaheadInput: typeaheadQuery,
                userTypeaheadInput,
                geocoderLatLng: geocoderLatLng.success,
                geocoderValid
            });
        } else {
            log('info', 'location - typeahead on next click', {
                autocompleteSelectedTypeaheadInput:
                  typeaheadQuery === userTypeaheadInput ? null : typeaheadQuery,
                userTypeaheadInput,
                geocoderLatLng:
                  typeaheadQuery === userTypeaheadInput ? null : geocoderLatLng.success,
                geocoderValid:
                  typeaheadQuery === userTypeaheadInput || !geocoderLatLng.success
                      ? null
                      : geocoderValid
            });
        }

        trigger('interaction', logLocationInformationEvent(address, pageNameDetailed, brand));
        pageFlow.next(isAAP && AAPLOCATION_PAGE);
    };

    useEffect(() => {
        const initialLoad = async () => {
            if (!isMobileView()) window.scrollTo(0, 0);
            const config = getConfigFromLocale(locale);
            trigger('pageLoad', { pageName: 'partner-qualifier: Location', referrer });
            trigger('pageview', createPageViewEvent('Location', pageNameDetailed));
            analytics.setAnalyticsPageName('JoinFormAddressInfo');
            analytics.viewTTIstop();
            trigger('pageLoadGtm', { pageName: 'PropertyAddress', event: 'PropertyAddressLoad' });
            const googleApiLoader = await GoogleApiLoader.load(config);
            if (googleApiLoader) {
                setMapsApi(googleApiLoader);
            }
        };

        initialLoad();

        return () => {
            analytics.viewTTIstart();
            trigger('pageUnload', { referrer });
        };
    }, []);

    useEffect(() => {
        if (isSubmitting
            && (unitValidationSuccess || unitValidationFail)
            && !unitValidationStart) {
            if (uNValidation.missingUnitNumber || !uNValidation.unitNumberValid) {
                moveOn();
            } else {
                triggerGeoCoderValidation();
            }
        }
    }, [
        unitValidationSuccess,
        unitValidationStart,
        unitValidationFail
    ]);

    useEffect(() => {
        if (isSubmitting
            && (geocoderSuccess || geocoderFail)
            && !geocoderStart) {
            setMoveout(true);
        }
    }, [
        geocoderSuccess,
        geocoderStart,
        geocoderFail
    ]);

    useEffect(() => {
        if (moveout) {
            moveOn();
        }
    }, [moveout]);

    const handleNextClick = () => {
        setIsSubmitting(true);
        trigger('genericClick', {
            category: 'navigation',
            action: 'Location: nextButton'
        });
        if (isFormDataValid()) {
            if (unitCheckEnabled) {
                triggerValidateUnitNumber();
                return;
            }
            triggerGeoCoderValidation();
            return;
        }
        moveOn();
    };

    const config = {
        region: locale.canonical.substring(3),
        lang: locale.canonical.substring(0, 2)
    };
    const classes = classnames('ulx-location-wrapper', 'ulx-animated', 'ulx-animated--movein', { 'ulx-animated--moveout': moveout });

    // eslint-disable-next-line no-nested-ternary
    const MapToUse = isAddressV2 ? React.Fragment : MovableMap;

    return (
        <div className={classnames('form-container')}>
            <div className={classes}>
                <div className="ulx-location-wrapper__content">
                    <ProgressBarLYP step={step} />
                    <h2>{title}</h2>
                    {
                        instructions && (
                            <p>
                                {instructions}
                            </p>
                        )
                    }
                    <div className="ulx-typeahead-address-group">
                        <LocationTypeahead
                            mapsApi={mapsApi}
                            config={config}
                            updateUserTypeaheadInput={setUserTypeaheadInput}
                        />
                        <LocationField
                            key={unitNumberKey}
                            fieldLabelKey={unitNumberKey}
                            fieldLabelTranslation={unitNumberLabel}
                            value={address.address2}
                            onChangeHandler={handleAddressFieldChange}
                            hide={!unitCheckEnabled}
                        />

                    </div>
                    <MapToUse
                        mapsApi={mapsApi}
                        lat={latLng.lat}
                        lng={latLng.lng}
                        text={mapFootNote}
                        trigger={trigger}
                        onPositionChange={setLatLng}
                        address={address}
                        pageName={pageNameDetailed}
                        brand={brand}
                        display={!isAddressV2}
                    />
                    <NextButton
                        submitText={submitText}
                        onClick={handleNextClick}
                        disabled={isSubmitting || geocoderLatLng.start}
                        isConventionalLodging={features.isConventionalLodgingFlow}
                    />
                </div>
            </div>
        </div>
    );
}

Location.defaultProps = {
    typeaheadQuery: undefined,
    isAAP: false
};

Location.propTypes = {
    brand: PropTypes.string.isRequired,
    mapFootNote: PropTypes.string.isRequired,
    triggerGeoCoderValidation: PropTypes.func.isRequired,
    geocoderValidation: PropTypes.shape({
        value: PropTypes.bool,
        errorList: PropTypes.arrayOf(PropTypes.string),
        start: PropTypes.bool,
        success: PropTypes.bool,
        fail: PropTypes.bool
    }).isRequired,
    triggerValidateUnitNumber: PropTypes.func.isRequired,
    referrer: PropTypes.string.isRequired,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired
    }).isRequired,
    latLng: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number
    }).isRequired,
    setLatLng: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    pageFlow: PropTypes.shape({
        next: PropTypes.func.isRequired
    }).isRequired,
    submitText: PropTypes.string.isRequired,
    locale: PropTypes.shape({
        canonical: PropTypes.string,
        display: PropTypes.string
    }).isRequired,
    step: PropTypes.number.isRequired,
    address: PropTypes.shape({
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        stateProvince: PropTypes.string,
        postalCode: PropTypes.string,
        country: PropTypes.string
    }).isRequired,
    features: PropTypes.shape({
        isConventionalLodgingFlow: PropTypes.bool.isRequired
    }).isRequired,
    analytics: PropTypes.shape({
        viewTTIstart: PropTypes.func,
        viewTTIstop: PropTypes.func,
        setAnalyticsPageName: PropTypes.func
    }).isRequired,
    addressValidation: PropTypes.shape({
        address1: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        address2: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        city: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        stateProvince: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        postalCode: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        country: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        })
    }).isRequired,
    geocoderLatLng: PropTypes.shape({
        value: PropTypes.shape({
            lat: PropTypes.number,
            lng: PropTypes.number
        }),
        start: PropTypes.bool,
        success: PropTypes.bool,
        fail: PropTypes.bool
    }).isRequired,
    unitNumberValidation: PropTypes.shape({
        value: PropTypes.shape({
            missingUnitNumber: PropTypes.bool,
            number: PropTypes.string,
            unitNumberValid: PropTypes.bool
        }),
        start: PropTypes.bool,
        success: PropTypes.bool,
        fail: PropTypes.bool
    }).isRequired,
    unitCheckEnabled: PropTypes.bool.isRequired,
    unitNumberLabel: PropTypes.string.isRequired,
    setAddressField: PropTypes.func.isRequired,
    pathSource: PropTypes.string.isRequired,
    instructions: PropTypes.string.isRequired,
    isAddressV2: PropTypes.bool.isRequired,
    typeaheadQuery: PropTypes.string,
    isAAP: PropTypes.bool
};

/* istanbul ignore next */
const mapStateToProps = (state) => {
    const { location, app } = state;
    const {
        referrer,
        loggedInUserType,
        features,
        brand,
        pathSource
    } = app;
    const {
        latLng,
        typeaheadQuery,
        typeaheadPredictions,
        address,
        addressValidation,
        geocoderValidation,
        geocoderLatLng,
        unitNumberValidation
    } = location;
    const translations = new TranslationUtil(app.translations, app.fallbackTranslations);
    const hasQueryInput = !!typeaheadQuery;
    const step = features.isLocationFirst ? 1 : 2;
    const experimentUtil = new ExperimentUtil(app.experiments);
    const unitCheckEnabled = features.isFrboFlow;
    const isAddressV2 = features.isConventionalLodgingFlow || isConfirmAddressFeatureEnabled(experimentUtil);
    const instructions = features.isConventionalLodgingFlow && translations.getTranslation('Cl.locationTypeahead.property.instructions');
    const contentMessagingUpdatesEnabled = isEnUsPermanentFeatureVrEnabled(experimentUtil);

    return {
        brand,
        mapFootNote: translations.getTranslation('Vrbo.lyp.manualLocation.mapFootNote'),
        geocoderLatLng,
        unitNumberValidation,
        referrer,
        latLng,
        title: translations.getTranslation(contentMessagingUpdatesEnabled ? 'lyp.location.titleTest' : 'lyp.location.title'),
        predictions: typeaheadPredictions,
        loggedInUserType,
        submitText: translations.getTranslation('Vrbo.cta.next'),
        addAddress: translations.getTranslation('lyp.location.addAddress'),
        locale: app.locale,
        hasQueryInput,
        step,
        features,
        address,
        addressValidation,
        geocoderValidation,
        unitCheckEnabled,
        unitNumberLabel: translations.getTranslation('Vrbo.lyp.manualLocation.address2'),
        pathSource,
        instructions,
        isAddressV2,
        typeaheadQuery
    };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch, ownProps) => ({
    triggerGeoCoderValidation: () => {
        dispatch(LocationActions.triggerGeoCoderValidation());
    },
    setLatLng: (latLong) => dispatch(LocationActions.setLatLng(latLong)),
    triggerValidateUnitNumber: () => {
        dispatch(LocationActions.triggerUnitNumberValidation());
    },
    setAddressField:
    (fieldName, value) => {
        dispatch(LocationActions.setAddressField(
            fieldName,
            value,
            ownProps.features.isLatinInputEnabled
        ));
    }
});

const ConnectedLocation = connect(mapStateToProps, mapDispatchToProps)(Location);
ConnectedLocation.displayName = 'Location';

export default hot(module)(withPageFlow(ConnectedLocation));
